/**
 * Phase 2.5 of the Vue 3 migration: thin compatibility shims for the
 * residual `<v-*>` tags. Each shim re-implements just enough of Vuetify
 * 1.5's API surface — the props the Edaptio codebase actually exercises
 * — using plain HTML elements with Tailwind classes. v-model bindings,
 * `@click`, `@input`, slots, and disabled / readonly are all forwarded.
 *
 * Components are registered globally under a `v-*` kebab-case name so the
 * codemod is a no-op (the templates keep their `<v-text-field>` / `<v-tooltip>`
 * markup; the only change is which component renders for it). When
 * Vue.use(Vuetify, …) comes out in Phase 2.6, these shims become the
 * authoritative implementation for any remaining call sites.
 *
 * Each shim is intentionally minimal. Visual harness deltas drive
 * refinement on a per-site basis as they surface — pragmatic, not
 * pixel-perfect parity with Vuetify 1.5.
 *
 * Phase 6: rewritten to v3-native render functions. `render(h)` →
 * `render()` with imported `h`; flat data objects (no `attrs/domProps/on`
 * nesting); `onXxx` event keys; `this.$slots.X()` (no $scopedSlots);
 * `value`/`@input` v-model → `modelValue`/`update:modelValue`. The shims
 * accept BOTH `value` and `modelValue` props on form controls so legacy
 * call sites that explicitly bind `:value=` (rather than v-model) keep
 * working unchanged.
 */
import { h } from 'vue'
import { nextUid } from '@/utils/uid'

// Vue 3's v-model on a component compiles to `:modelValue` +
// `@update:modelValue` by default. We accept both `value` (legacy
// explicit binding) and `modelValue` (the v3 v-model target) to keep
// every existing call site working without template edits. The shim
// always emits BOTH `input` (legacy) and `update:modelValue` (v3-native)
// so parent listeners — explicit `@input=` or implicit v-model — both fire.
/**
 * Vuetify appended `px` to bare numeric dimensions regardless of whether the
 * template passed a number (`:max-width="600"`) or a string (`max-width="600"`).
 * A static attribute always arrives as a string, and emitting unitless
 * `max-width: 600` is invalid CSS that the CSSOM silently drops — which made
 * `<v-dialog max-width="600">` render edge-to-edge. Pass through anything that
 * already carries a unit (`60%`, `40rem`).
 */
function cssLength(value) {
    if (value === null || value === undefined || value === '') return undefined
    return /^\d+(\.\d+)?$/.test(String(value).trim()) ? `${value}px` : String(value)
}

function modelMixin(props = {}) {
    return {
        modelValue: { type: props.type || [String, Number, Boolean, Array, Object], default: props.default ?? '' },
        value: { type: props.type || [String, Number, Boolean, Array, Object], default: undefined },
    }
}

function emitModel(self, val) {
    self.$emit('input', val)
    self.$emit('update:modelValue', val)
}

function readModel(self) {
    // modelValue takes precedence (v-model in v3); fall back to legacy `value`.
    return self.modelValue !== undefined && self.modelValue !== ''
        ? self.modelValue
        : (self.value !== undefined ? self.value : self.modelValue)
}

// ─── Validation ────────────────────────────────────────────────────────

/**
 * `<v-form v-model="valid">` + `:rules` on the fields inside it.
 *
 * The original shims declared a `rules` prop but never ran it, and VForm never
 * emitted `update:modelValue` — so `valid` stayed `false` forever. Call sites
 * that gate their submit handler on it (`if (!this.validFormUser) return 0`)
 * had a button that did nothing at all when clicked, with no error shown:
 * that's the dead "Gem" button on /admin/institutions/:id/users.
 *
 * A form provides a reactive uid→boolean map; every validatable field inside
 * it registers on mount, keeps its entry current, and deregisters on unmount.
 * The form is valid when every entry is true (an empty form is valid, matching
 * Vuetify).
 */
const FORM_VALIDITY = Symbol('vFormValidity')

const validatable = {
    inject: {
        formValidity: { from: FORM_VALIDITY, default: null },
    },
    computed: {
        /** Messages from failing `:rules` — '' for a rule that returned bare false. */
        ruleErrors() {
            return (this.rules || []).reduce((acc, rule) => {
                if (typeof rule !== 'function') return acc
                let result
                try {
                    result = rule(this.currentValue)
                } catch (e) {
                    // Rules like `v => v.length > 2` throw on null/undefined —
                    // and Postgres hands back NULL for empty text columns. Treat
                    // a throwing rule as "not satisfied" rather than letting it
                    // take down the render.
                    result = false
                }
                if (result !== true) acc.push(typeof result === 'string' ? result : '')
                return acc
            }, [])
        },
        /** Explicit `error-messages` plus any rule failures that carry text. */
        errs() {
            const e = this.errorMessages
            const explicit = Array.isArray(e) ? e : (e ? [e] : [])
            return [...explicit, ...this.ruleErrors.filter(Boolean)]
        },
        isValid() {
            return this.ruleErrors.length === 0
        },
    },
    created() {
        this.fieldUid = `field-${nextUid()}`
    },
    mounted() {
        if (this.formValidity) this.formValidity[this.fieldUid] = this.isValid
    },
    unmounted() {
        if (this.formValidity) delete this.formValidity[this.fieldUid]
    },
    watch: {
        isValid(valid) {
            if (this.formValidity) this.formValidity[this.fieldUid] = valid
        },
    },
}

// ─── Form controls ─────────────────────────────────────────────────────

const VTextField = {
    name: 'VTextField',
    mixins: [validatable],
    inheritAttrs: false,
    props: {
        modelValue: { type: [String, Number], default: '' },
        value: { type: [String, Number], default: undefined },
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        type: { type: String, default: 'text' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        rules: { type: Array, default: () => [] },
        errorMessages: { type: [String, Array], default: '' },
        hideDetails: { type: Boolean, default: false },
        prependIcon: { type: String, default: '' },
        appendIcon: { type: String, default: '' },
        outline: { type: Boolean, default: false },
        outlined: { type: Boolean, default: false },
        solo: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        autofocus: { type: Boolean, default: false },
    },
    emits: ['input', 'update:modelValue'],
    computed: {
        currentValue() {
            return this.value !== undefined ? this.value : this.modelValue
        },
        wrapperClass() {
            const c = ['v-text-field-shim', 'tw-flex', 'tw-flex-col', 'tw-w-full']
            if (this.$attrs.class) c.push(this.$attrs.class)
            return c
        },
        inputClass() {
            const c = [
                'tw-w-full', 'tw-px-3', 'tw-py-2', 'tw-text-base',
                'tw-bg-white', 'tw-border', 'tw-border-light-gray',
                'tw-rounded-s',
                'focus:tw-border-primary', 'focus:tw-outline-none',
            ]
            if (this.errs.length) c.push('tw-border-red')
            if (this.disabled) c.push('tw-opacity-50', 'tw-cursor-not-allowed')
            return c
        },
    },
    render() {
        const children = []
        if (this.label) children.push(h('label', { class: 'tw-text-sm tw-text-gray tw-mb-1' }, this.label))
        children.push(h('input', {
            class: this.inputClass,
            type: this.type,
            placeholder: this.placeholder,
            disabled: this.disabled,
            readonly: this.readonly,
            value: this.currentValue,
            autofocus: this.autofocus,
            onInput: (e) => {
                this.$emit('input', e.target.value)
                this.$emit('update:modelValue', e.target.value)
            },
        }))
        if (!this.hideDetails && this.errs.length) {
            children.push(h('span', { class: 'tw-text-xs tw-text-red tw-mt-1' }, this.errs.join(' ')))
        }
        return h('div', { class: this.wrapperClass }, children)
    },
}

const VTextarea = {
    ...VTextField,
    name: 'VTextarea',
    props: { ...VTextField.props, rows: { type: [String, Number], default: 4 } },
    render() {
        const children = []
        if (this.label) children.push(h('label', { class: 'tw-text-sm tw-text-gray tw-mb-1' }, this.label))
        children.push(h('textarea', {
            class: this.inputClass,
            placeholder: this.placeholder,
            disabled: this.disabled,
            readonly: this.readonly,
            rows: this.rows,
            value: this.currentValue,
            onInput: (e) => {
                this.$emit('input', e.target.value)
                this.$emit('update:modelValue', e.target.value)
            },
        }))
        if (!this.hideDetails && this.errs.length) {
            children.push(h('span', { class: 'tw-text-xs tw-text-red tw-mt-1' }, this.errs.join(' ')))
        }
        return h('div', { class: this.wrapperClass }, children)
    },
}

const VSelect = {
    name: 'VSelect',
    inheritAttrs: false,
    mixins: [validatable],
    props: {
        modelValue: { type: [String, Number, Array, Object], default: null },
        value: { type: [String, Number, Array, Object], default: undefined },
        rules: { type: Array, default: () => [] },
        errorMessages: { type: [String, Array], default: '' },
        items: { type: Array, default: () => [] },
        label: { type: String, default: '' },
        itemText: { type: String, default: 'text' },
        itemValue: { type: String, default: 'value' },
        disabled: { type: Boolean, default: false },
        multiple: { type: Boolean, default: false },
        chips: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },
        hideDetails: { type: Boolean, default: false },
        outlined: { type: Boolean, default: false },
        solo: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
    },
    emits: ['input', 'update:modelValue'],
    computed: {
        currentValue() {
            return this.value !== undefined ? this.value : this.modelValue
        },
    },
    methods: {
        getText(opt) { return typeof opt === 'object' ? opt[this.itemText] : String(opt) },
        getValue(opt) { return typeof opt === 'object' ? opt[this.itemValue] : opt },
    },
    render() {
        const children = []
        if (this.label) children.push(h('label', { class: 'tw-text-sm tw-text-gray tw-mb-1' }, this.label))
        const optionEls = this.items.map((item) => h('option', {
            value: this.getValue(item),
            selected: this.getValue(item) === this.currentValue,
        }, this.getText(item)))
        if (this.placeholder && !this.currentValue) {
            optionEls.unshift(h('option', { value: '', disabled: true, selected: true }, this.placeholder))
        }
        children.push(h('select', {
            class: [
                'tw-w-full', 'tw-px-3', 'tw-py-2', 'tw-bg-white',
                'tw-border', 'tw-border-light-gray', 'tw-rounded-s',
                'focus:tw-border-primary', 'focus:tw-outline-none',
                this.disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : '',
            ],
            disabled: this.disabled,
            multiple: this.multiple,
            onChange: (e) => {
                this.$emit('input', e.target.value)
                this.$emit('update:modelValue', e.target.value)
            },
        }, optionEls))
        if (!this.hideDetails && this.errs.length) {
            children.push(h('span', { class: 'tw-text-xs tw-text-red tw-mt-1' }, this.errs.join(' ')))
        }
        return h('div', { class: ['tw-flex', 'tw-flex-col', 'tw-w-full', this.$attrs.class].filter(Boolean) }, children)
    },
}

const VAutocomplete = { ...VSelect, name: 'VAutocomplete' }

const VCheckbox = {
    name: 'VCheckbox',
    inheritAttrs: false,
    props: {
        modelValue: { type: Boolean, default: false },
        value: { type: Boolean, default: undefined },
        inputValue: { type: Boolean, default: undefined },
        label: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        hideDetails: { type: Boolean, default: false },
    },
    emits: ['input', 'update:modelValue', 'change'],
    computed: {
        // Vuetify's <v-checkbox> distinguishes between v-model (`inputValue`)
        // and a discriminator `value`. In our codebase only the v-model usage
        // matters; treat any of the three as the checked state.
        isChecked() {
            if (this.inputValue !== undefined) return !!this.inputValue
            if (this.value !== undefined) return !!this.value
            return !!this.modelValue
        },
    },
    render() {
        return h('label', {
            class: ['tw-inline-flex', 'tw-items-center', 'tw-gap-2', this.$attrs.class].filter(Boolean),
        }, [
            h('input', {
                type: 'checkbox',
                disabled: this.disabled,
                checked: this.isChecked,
                onChange: (e) => {
                    this.$emit('input', e.target.checked)
                    this.$emit('update:modelValue', e.target.checked)
                    this.$emit('change', e.target.checked)
                },
            }),
            this.label ? h('span', { class: 'tw-text-sm' }, this.label) : null,
        ])
    },
}

const VSwitch = {
    name: 'VSwitch',
    extends: VCheckbox,
    render() {
        return h('label', {
            class: ['tw-inline-flex', 'tw-items-center', 'tw-gap-2', this.$attrs.class].filter(Boolean),
        }, [
            h('span', {
                class: [
                    'tw-relative', 'tw-inline-block', 'tw-w-10', 'tw-h-6',
                    'tw-rounded-full', 'tw-transition-colors',
                    this.isChecked ? 'tw-bg-primary' : 'tw-bg-gray-30',
                ],
            }, [
                h('input', {
                    class: 'tw-opacity-0 tw-absolute tw-inset-0 tw-cursor-pointer',
                    type: 'checkbox',
                    disabled: this.disabled,
                    checked: this.isChecked,
                    onChange: (e) => {
                        this.$emit('input', e.target.checked)
                        this.$emit('update:modelValue', e.target.checked)
                        this.$emit('change', e.target.checked)
                    },
                }),
                h('span', {
                    class: [
                        'tw-absolute', 'tw-top-0.5', 'tw-w-5', 'tw-h-5',
                        'tw-bg-white', 'tw-rounded-full', 'tw-shadow', 'tw-transition-transform',
                        this.isChecked ? 'tw-translate-x-4' : 'tw-translate-x-0.5',
                    ],
                }),
            ]),
            this.label ? h('span', { class: 'tw-text-sm' }, this.label) : null,
        ])
    },
}

const VRadio = {
    name: 'VRadio',
    inheritAttrs: false,
    props: {
        label: { type: String, default: '' },
        value: { default: null },
    },
    inject: { radioGroup: { default: null } },
    computed: {
        isChecked() { return this.radioGroup ? this.radioGroup.modelValue === this.value : false },
    },
    render() {
        return h('label', { class: ['tw-inline-flex', 'tw-items-center', 'tw-gap-2'] }, [
            h('input', {
                type: 'radio',
                name: this.radioGroup ? this.radioGroup.groupName : undefined,
                value: this.value,
                checked: this.isChecked,
                onChange: () => this.radioGroup && this.radioGroup.select(this.value),
            }),
            this.label ? h('span', { class: 'tw-text-sm' }, this.label) : null,
        ])
    },
}

const VRadioGroup = {
    name: 'VRadioGroup',
    props: {
        modelValue: { default: null },
        value: { default: undefined },
    },
    emits: ['input', 'update:modelValue'],
    data() { return { uid: nextUid() } },
    computed: {
        currentValue() {
            return this.value !== undefined ? this.value : this.modelValue
        },
    },
    provide() {
        const self = this
        return {
            radioGroup: {
                get modelValue() { return self.currentValue },
                groupName: `radio-group-${self.uid}`,
                select(v) {
                    self.$emit('input', v)
                    self.$emit('update:modelValue', v)
                },
            },
        }
    },
    render() {
        return h('div', { class: 'tw-flex tw-flex-col tw-gap-2' }, this.$slots.default ? this.$slots.default() : [])
    },
}

const VForm = {
    name: 'VForm',
    props: {
        modelValue: { type: Boolean, default: false },
        value: { type: Boolean, default: undefined },
    },
    emits: ['submit', 'input', 'update:modelValue'],
    data() {
        // Reactive uid→boolean map, filled in by the validatable fields below
        // this form via provide/inject. Plain reactive data (not a ref) so
        // fields can write straight into it.
        return { fieldValidity: {} }
    },
    provide() {
        return { [FORM_VALIDITY]: this.fieldValidity }
    },
    computed: {
        isValid() {
            return Object.values(this.fieldValidity).every(Boolean)
        },
    },
    watch: {
        isValid: {
            immediate: true,
            handler(valid) { emitModel(this, valid) },
        },
    },
    render() {
        return h('form', {
            onSubmit: (e) => { e.preventDefault(); this.$emit('submit', e) },
        }, this.$slots.default ? this.$slots.default() : [])
    },
    methods: {
        // Vuetify exposes validate(); legacy code calls `this.$refs.form.validate()`.
        validate() { return this.isValid },
        reset() {},
        resetValidation() {},
    },
}

// ─── Overlays ──────────────────────────────────────────────────────────

const VTooltip = {
    name: 'VTooltip',
    props: {
        top: { type: Boolean, default: false },
        bottom: { type: Boolean, default: false },
        left: { type: Boolean, default: false },
        right: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
    },
    data: () => ({ hovered: false }),
    render() {
        const tipPos = this.top ? '-tw-top-8 tw-left-1/2 -tw-translate-x-1/2'
            : this.bottom ? 'tw-top-full tw-left-1/2 -tw-translate-x-1/2 tw-mt-1'
            : this.left ? 'tw-right-full tw-top-1/2 -tw-translate-y-1/2 tw-mr-1'
            : this.right ? 'tw-left-full tw-top-1/2 -tw-translate-y-1/2 tw-ml-1'
            : '-tw-top-8 tw-left-1/2 -tw-translate-x-1/2'
        // Activator slot: Vue 3 has a unified `$slots` API; both
        // <template v-slot:activator="{ on }"> and a non-scoped
        // <template v-slot:activator> work via $slots.activator(...).
        const activator = this.$slots.activator
            ? this.$slots.activator({ on: { mouseenter: () => { this.hovered = true }, mouseleave: () => { this.hovered = false } } })
            : []
        return h('span', { class: 'tw-relative tw-inline-block' }, [
            ...activator,
            !this.disabled && this.hovered ? h('span', {
                class: ['tw-absolute', 'tw-z-50', 'tw-px-2', 'tw-py-1', 'tw-text-xs', 'tw-rounded', 'tw-bg-mine-shaft', 'tw-text-white', 'tw-whitespace-nowrap', tipPos],
            }, this.$slots.default ? this.$slots.default() : []) : null,
        ])
    },
}

const VDialog = {
    name: 'VDialog',
    props: {
        modelValue: { type: Boolean, default: false },
        value: { type: Boolean, default: undefined },
        persistent: { type: Boolean, default: false },
        maxWidth: { type: [String, Number], default: 600 },
        // Vuetify 1.5's `fullscreen` made the dialog cover the viewport and
        // drop its max-width. The shim originally left the prop out, so the
        // ten `:fullscreen="true"` call sites (cleanModal and friends) all
        // rendered as a max-width card instead — the corrected-file viewer
        // opened at 50% of the window and cropped the PDF (HF&VUC Nord,
        // 18 Aug 2026).
        fullscreen: { type: Boolean, default: false },
    },
    emits: ['input', 'update:modelValue'],
    computed: {
        isOpen() {
            return this.value !== undefined ? this.value : this.modelValue
        },
    },
    render() {
        if (!this.isOpen) return null
        return h('div', {
            class: 'tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50',
            // The app navbar is `.sticky` at z-index 60, so a dialog at z-50
            // renders underneath it. A fullscreen dialog starts at y=0, which
            // puts its whole header row — title, close, Download — behind the
            // navbar and out of reach. Vuetify's dialog sat at ~200; match it
            // for the fullscreen case so the chrome is clickable again.
            style: this.fullscreen ? { zIndex: 200 } : null,
            onClick: (e) => {
                if (!this.persistent && e.target === e.currentTarget) {
                    this.$emit('input', false)
                    this.$emit('update:modelValue', false)
                }
            },
        }, [
            h('div', {
                class: this.fullscreen
                    ? 'tw-bg-white tw-overflow-hidden tw-w-full tw-h-full'
                    : 'tw-bg-white tw-rounded tw-shadow-card tw-overflow-hidden',
                // A fullscreen dialog takes the whole viewport, so it gets no
                // max-width — and no rounding/shadow, since there is nothing
                // left of the backdrop to sit on.
                style: this.fullscreen
                    ? {}
                    : { maxWidth: cssLength(this.maxWidth), width: '100%' },
            }, this.$slots.default ? this.$slots.default() : []),
        ])
    },
}

const VMenu = {
    name: 'VMenu',
    props: {
        modelValue: { type: Boolean, default: false },
        value: { type: Boolean, default: undefined },
        closeOnContentClick: { type: Boolean, default: true },
    },
    emits: ['input', 'update:modelValue'],
    data: () => ({ openLocal: false }),
    computed: {
        isOpen() {
            const ext = this.value !== undefined ? this.value : this.modelValue
            return ext || this.openLocal
        },
    },
    methods: {
        setOpen(v) {
            this.openLocal = v
            this.$emit('input', v)
            this.$emit('update:modelValue', v)
        },
    },
    render() {
        const activator = this.$slots.activator
            ? this.$slots.activator({ on: { click: () => { this.setOpen(!this.isOpen) } } })
            : []
        return h('span', { class: 'tw-relative tw-inline-block' }, [
            ...activator,
            this.isOpen ? h('div', {
                class: 'tw-absolute tw-z-50 tw-mt-1 tw-bg-white tw-rounded tw-shadow-card',
                onClick: this.closeOnContentClick ? () => { this.setOpen(false) } : null,
            }, this.$slots.default ? this.$slots.default() : []) : null,
        ])
    },
}

// ─── Behaviour-bound ──────────────────────────────────────────────────

const VHover = {
    name: 'VHover',
    data: () => ({ hover: false }),
    render() {
        const slot = this.$slots.default
        if (!slot) return null
        return h('div', {
            onMouseenter: () => { this.hover = true },
            onMouseleave: () => { this.hover = false },
        }, slot({ hover: this.hover }))
    },
}

// Vuetify theme colour names used with v-progress-circular. Anything else is
// passed through as a CSS colour (#hex, rgb(), var(--x)).
const PROGRESS_COLORS = {
    primary: 'var(--primary)',
    secondary: 'var(--secondary)',
    accent: 'var(--accent)',
    completedGreen: '#05D48A',
}
const progressColor = (c) => PROGRESS_COLORS[c] || (/^(#|rgb|hsl|var\()/.test(c || '') ? c : 'currentColor')

// Indeterminate: the spinning border it always was. Determinate (a `value`,
// as percentageCircle passes): an SVG ring like Vuetify's. Until 2026-11-03
// the shim ignored `value` and drew the spinner for every percentage circle,
// which `.full-width` (width: 100% !important) then stretched into an empty
// oval. The SVG keeps its viewBox, so a wider box only centres the ring.
const VProgressCircular = {
    name: 'VProgressCircular',
    props: {
        size: { type: [String, Number], default: 32 },
        width: { type: [String, Number], default: 4 },
        color: { type: String, default: 'primary' },
        indeterminate: { type: Boolean, default: false },
        value: { type: [String, Number], default: undefined },
        modelValue: { type: [String, Number], default: undefined },
        rotate: { type: [String, Number], default: 0 },
    },
    computed: {
        normalizedValue() {
            const v = Number(this.value !== undefined ? this.value : this.modelValue)
            return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0
        },
    },
    render() {
        const sz = typeof this.size === 'number' ? `${this.size}px` : this.size
        const slot = this.$slots.default ? this.$slots.default() : []
        if (this.indeterminate) {
            return h('span', {
                class: ['tw-inline-block', 'tw-rounded-full', 'tw-border-4', 'tw-border-light-gray', 'v-progress-spin'],
                style: { width: sz, height: sz, borderTopColor: 'currentColor' },
            }, slot)
        }
        const box = Number.parseFloat(this.size) || 32
        const stroke = Math.min(Number.parseFloat(this.width) || 4, box / 2)
        const r = (box - stroke) / 2
        const circumference = 2 * Math.PI * r
        const ring = (extra) => h('circle', {
            cx: box / 2, cy: box / 2, r, fill: 'none', 'stroke-width': stroke, ...extra,
        })
        return h('div', {
            class: 'v-progress-circular',
            role: 'progressbar',
            'aria-valuemin': 0,
            'aria-valuemax': 100,
            'aria-valuenow': this.normalizedValue,
            style: { position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: sz, height: sz },
        }, [
            h('svg', {
                viewBox: `0 0 ${box} ${box}`,
                'aria-hidden': 'true',
                style: { position: 'absolute', inset: 0, width: '100%', height: '100%', transform: `rotate(${Number(this.rotate) - 90}deg)` },
            }, [
                ring({ stroke: '#E9ECEF' }),
                ring({
                    stroke: progressColor(this.color),
                    'stroke-dasharray': circumference,
                    'stroke-dashoffset': circumference * (1 - this.normalizedValue / 100),
                    style: { transition: 'stroke-dashoffset 0.4s ease-out' },
                }),
            ]),
            h('div', { style: { position: 'relative' } }, slot),
        ])
    },
}

const VProgressLinear = {
    name: 'VProgressLinear',
    props: {
        modelValue: { type: [String, Number], default: 0 },
        value: { type: [String, Number], default: undefined },
        color: { type: String, default: 'primary' },
        indeterminate: { type: Boolean, default: false },
        height: { type: [String, Number], default: 4 },
    },
    computed: {
        currentValue() {
            return this.value !== undefined ? this.value : this.modelValue
        },
    },
    render() {
        const w = this.indeterminate ? '100%' : `${this.currentValue}%`
        const h_ = typeof this.height === 'number' ? `${this.height}px` : this.height
        return h('div', {
            class: ['tw-w-full', 'tw-bg-light-gray', 'tw-overflow-hidden', 'tw-rounded'],
            style: { height: h_ },
        }, [
            h('div', { class: `tw-bg-${this.color} tw-h-full`, style: { width: w, transition: 'width 0.3s' } }),
        ])
    },
}

const VDataTable = {
    name: 'VDataTable',
    props: {
        items: { type: Array, default: () => [] },
        headers: { type: Array, default: () => [] },
        itemKey: { type: String, default: 'id' },
        hideDefaultFooter: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
    },
    render() {
        const headerCells = this.headers.map(hd => h('th', {
            class: 'tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-font-bodySemibold tw-border-b tw-border-light-gray',
        }, hd.text || hd.value))
        // Vuetify 1.x `headers` scoped slot. Several call sites (admin payments,
        // videos, geogebra, questionnaires, flashcards) render their own sortable
        // <th> row through this slot and never pass a `headers` prop — without
        // support here their table had no head AND no body-cell definitions, so
        // the page came up blank apart from its surrounding chrome.
        const headerRow = this.$slots.headers
            ? this.$slots.headers({ headers: this.headers })
            : h('tr', headerCells)
        const rowCells = (item) => this.headers.map(hd =>
            h('td', { class: 'tw-px-3 tw-py-2 tw-text-sm tw-border-b tw-border-light-gray' }, String(item[hd.value] ?? '')))
        const body = this.items.map(item =>
            // Vuetify 1.x scoped slot for custom row rendering
            this.$slots.items
                ? this.$slots.items({ item, props: { item } })
                : h('tr', { key: item[this.itemKey] }, rowCells(item)))
        // `v-data-table-shim` carries the cell baseline (padding, row rule,
        // alignment) from assets/css/tailwind.css. Rows supplied through the
        // items/headers slots are the call site's own <th>/<td> markup, which
        // only ever had Vuetify's table styling to lean on — without this they
        // render as a bare, unpadded HTML table.
        return h('table', { class: ['v-data-table-shim', 'tw-w-full', 'tw-bg-white', this.$attrs.class].filter(Boolean) }, [
            h('thead', [headerRow]),
            h('tbody', body),
            this.loading ? h('tfoot', [h('tr', [h('td', { colspan: this.headers.length }, 'Loading...')])]) : null,
        ])
    },
}

// ─── Registration ──────────────────────────────────────────────────────

const components = {
    VTextField, VTextarea, VSelect, VAutocomplete,
    VCheckbox, VSwitch, VRadio, VRadioGroup, VForm,
    VTooltip, VDialog, VMenu,
    VHover, VProgressCircular, VProgressLinear, VDataTable,
}

export default {
    install(app) {
        for (const [name, comp] of Object.entries(components)) {
            app.component(name, comp)
        }
    },
}
